import type { ConversationTurn, TurnMetadata } from '../api/types';

export function parseTurnMetadata(turn: ConversationTurn): TurnMetadata | null {
  if (!turn.metadata) return null;
  try {
    return JSON.parse(turn.metadata) as TurnMetadata;
  } catch {
    return null;
  }
}

export function extractSessionTargetItems(turns: ConversationTurn[]): string[] {
  const firstAssistant = turns.find(t => t.role === 'assistant');
  if (!firstAssistant) return [];
  const meta = parseTurnMetadata(firstAssistant);
  return meta?.session_target_items ?? [];
}

export function aggregateMetadata(turns: ConversationTurn[]) {
  const targetItems = extractSessionTargetItems(turns);
  const allHits = new Set<string>();
  const allNewWords = new Set<string>();
  const difficultyEvents: Array<{ turn: number; direction: 'up' | 'down' }> = [];
  const comprehensionLevels: Array<{ turn: number; level: 'low' | 'medium' | 'high' }> = [];

  for (const turn of turns) {
    const meta = parseTurnMetadata(turn);
    if (!meta) continue;
    meta.target_items_hit?.forEach(id => allHits.add(id));
    meta.new_items_introduced?.forEach(w => allNewWords.add(w));
    if (meta.difficulty_adjustment && meta.difficulty_adjustment !== 'none') {
      difficultyEvents.push({ turn: turn.turn_index, direction: meta.difficulty_adjustment });
    }
    if (meta.comprehension_level && turn.role === 'user') {
      comprehensionLevels.push({ turn: turn.turn_index, level: meta.comprehension_level });
    }
  }

  // Check which new words were "understood" — user used them in a later turn
  const userContent = turns.filter(t => t.role === 'user').map(t => t.content.toLowerCase());
  const newWordsUnderstood = [...allNewWords].filter(word =>
    userContent.some(content => content.includes(word.toLowerCase()))
  );

  return {
    targetItems,
    hitCount: allHits.size,
    hitRate: targetItems.length > 0 ? allHits.size / targetItems.length : 0,
    newWordsIntroduced: [...allNewWords],
    newWordsUnderstoodCount: newWordsUnderstood.length,
    difficultyEvents,
    comprehensionLevels,
    hasMetadata: difficultyEvents.length > 0 || comprehensionLevels.length > 0,
  };
}
